import { useState, useCallback, useMemo, useRef } from 'react';

import {
  useButtonPressedCallback,
  useClickstream
} from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import ExitConfirmModal from '#client/components/CheckDeposit/components/ExitConfirmModal';
import FormModal from '#client/components/FormModal';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useToastContext from '#client/hooks/useToastContext';
import { PATCH_APPLICATION_INIT_DATA_MUTATION } from '#client/mutations';

import { DialogContentStyled } from './AdditionalInfoModal.styles';
import {
  BusinessIndustryField,
  BusinessDescriptionField,
  SourceOfFundsField,
  AccountPurposeField,
  TransactionVolumeField,
  RegulatedStatusField,
  USNexusField
} from './components/Fields';

const includeIfNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

export const buildPatchApplicationInitPayload = ({ applicationId, formData }) => ({
  bankingApplicationId: applicationId,
  syncUnitCddEdd: true,
  businessIndustry: formData.businessIndustrySubtype || formData.businessIndustry,
  accountPurpose: formData.accountPurpose,
  ...(includeIfNonEmptyString(formData.accountPurposeDetail)
    ? { accountPurposeDetail: formData.accountPurposeDetail.trim() }
    : {}),
  sourceOfFunds: formData.sourceOfFunds,
  ...(includeIfNonEmptyString(formData.sourceOfFundsDescription)
    ? { sourceOfFundsDescription: formData.sourceOfFundsDescription.trim() }
    : {}),
  transactionVolume: formData.transactionVolume,
  ...(includeIfNonEmptyString(formData.transactionVolumeDescription)
    ? { transactionVolumeDescription: formData.transactionVolumeDescription.trim() }
    : {}),
  isRegulated: formData.isRegulated,
  ...(includeIfNonEmptyString(formData.regulatorName)
    ? { regulatorName: formData.regulatorName.trim() }
    : {}),
  usNexus: formData.usNexus ? formData.usNexusDetails.join(',') : 'notAvailable'
});

export const AdditionalInfoModal = ({ handleClose }) => {
  const { applicationId } = useApplicationStatusContext();
  const { createCommonErrorToast, createSuccessToast } = useToastContext();
  const { openModal, clearAllModal } = useModalContext();
  const [loading, setLoading] = useState(false);
  const isDirty = useRef(false);

  useModalDisplayTracker({
    elementName: 'additional_info_modal'
  });

  const trackCloseClick = useButtonPressedCallback({
    elementName: 'additional_info_close'
  });

  const trackExitConfirmCancel = useButtonPressedCallback({
    elementName: 'additional_info_exit_confirm_yes'
  });

  const trackExitConfirmKeepGoing = useButtonPressedCallback({
    elementName: 'additional_info_exit_confirm_no'
  });

  const handleCloseAndClearModals = useCallback(() => {
    clearAllModal();
  }, [clearAllModal]);

  const handleCloseModal = useCallback(() => {
    trackCloseClick();
    if (isDirty.current) {
      openModal({
        component: ExitConfirmModal,
        name: MODAL_NAMES.ADDITIONAL_INFO_EXIT_CONFIRM_MODAL,
        title: 'Are you sure?',
        description: 'If you leave now, you\u2019ll lose your progress and have to start over.',
        okText: 'Yes, cancel',
        cancelText: 'No, keep going',
        onOk: () => {
          trackExitConfirmCancel();
          handleCloseAndClearModals();
        },
        callback: () => {
          trackExitConfirmKeepGoing();
        }
      });
    } else {
      handleClose();
    }
  }, [
    openModal,
    handleClose,
    handleCloseAndClearModals,
    trackCloseClick,
    trackExitConfirmCancel,
    trackExitConfirmKeepGoing
  ]);

  const [formData, setFormData] = useState({
    businessIndustry: '',
    businessIndustrySubtype: '',
    businessDescription: '',
    sourceOfFunds: '',
    sourceOfFundsDescription: '',
    accountPurpose: '',
    accountPurposeDetail: '',
    transactionVolume: '',
    transactionVolumeDescription: '',
    isRegulated: null,
    regulatorName: '',
    usNexus: true,
    usNexusDetails: []
  });

  const [patchApplicationData] = useMutation(PATCH_APPLICATION_INIT_DATA_MUTATION, {
    noThrow: true
  });

  // Update form data when loaded
  const handleFieldChange = useCallback((field, value) => {
    isDirty.current = true;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Validate required fields
  const isFormValid = useMemo(() => {
    const requiredFields = [
      'businessIndustry',
      'businessDescription',
      'sourceOfFunds',
      'accountPurpose',
      'transactionVolume'
    ];

    // Check if all required fields are filled
    const basicFieldsValid = requiredFields.every((field) => formData[field]);

    // Check conditional fields
    const sourceOfFundsDescriptionRequired =
      (formData.sourceOfFunds === 'ImportExportRevenue' ||
        formData.sourceOfFunds === 'DonationsOrFundraising') &&
      !formData.sourceOfFundsDescription;

    const transactionVolumeDescriptionRequired =
      (formData.transactionVolume === 'Between1MAnd2M' ||
        formData.transactionVolume === 'GreaterThan2M') &&
      !formData.transactionVolumeDescription;

    const regulatorNameRequired = formData.isRegulated === true && !formData.regulatorName;

    const usNexusDetailsRequired =
      formData.usNexus === true && formData.usNexusDetails.length === 0;

    return (
      basicFieldsValid &&
      !sourceOfFundsDescriptionRequired &&
      !transactionVolumeDescriptionRequired &&
      !regulatorNameRequired &&
      !usNexusDetailsRequired &&
      formData.isRegulated !== null &&
      formData.usNexus !== null
    );
  }, [formData]);

  const clickstream = useClickstream();

  const trackSubmitOutcome = useCallback(
    (outcome) => {
      clickstream.buttonPressed({
        elementName: 'additional_info_submit_outcome',
        elementText: outcome
      });
    },
    [clickstream]
  );

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) {
      return;
    }

    setLoading(true);

    try {
      // applicationId comes from useApplicationStatusContext (InitialDataContext)
      if (!applicationId) {
        createCommonErrorToast({ error: 'Application data not available. Please try again.' });
        setLoading(false);
        return;
      }

      const submitData = buildPatchApplicationInitPayload({
        applicationId,
        formData
      });

      const { data, error } = await patchApplicationData({
        variables: {
          input: submitData
        }
      });

      if (error || data?.patchApplicationInitData?.status === 'Fail') {
        trackSubmitOutcome('failure');
        createCommonErrorToast({ error: error || 'Failed to submit information' });
      } else {
        trackSubmitOutcome('success');
        handleClose();
        // Show toast after modal closes so FormModal's clearAllToast doesn't remove it
        setTimeout(() => {
          createSuccessToast({
            title: 'Your details were submitted and saved'
          });
        }, 100);
      }
    } catch (err) {
      trackSubmitOutcome('failure');
      createCommonErrorToast({ error: err });
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    isFormValid,
    patchApplicationData,
    applicationId,
    createCommonErrorToast,
    createSuccessToast,
    handleClose,
    trackSubmitOutcome
  ]);

  const trackSubmitClick = useButtonPressedCallback({
    elementName: 'additional_info_submit'
  });

  const handleSubmitClick = () => {
    trackSubmitClick();
    handleSubmit();
  };

  return (
    <FormModal
      handleClose={handleCloseModal}
      titlesize="small"
    >
      <DialogContentStyled>
        <Box sx={{ maxWidth: '588px', margin: '0 auto', paddingTop: 5, paddingBottom: 5 }}>
          <Stack spacing={10}>
            {/* Header */}
            <Stack spacing={2}>
              <Heading
                family="secondary"
                size="3xl"
                weight="bold"
              >
                Additional information required for your account
              </Heading>
              <Text
                color="secondary"
                size="lg"
              >
                Confirm these details about your business so we can provide you with your account.
              </Text>
            </Stack>

            {/* Form Fields */}
            <Stack spacing={10}>
              <BusinessIndustryField
                onChange={(value) => handleFieldChange('businessIndustry', value)}
                onSubtypeChange={(value) => handleFieldChange('businessIndustrySubtype', value)}
                subtype={formData.businessIndustrySubtype}
                value={formData.businessIndustry}
              />

              <BusinessDescriptionField
                onChange={(value) => handleFieldChange('businessDescription', value)}
                value={formData.businessDescription}
              />

              <AccountPurposeField
                onChange={(value) => handleFieldChange('accountPurpose', value)}
                onDetailChange={(value) => handleFieldChange('accountPurposeDetail', value)}
                value={formData.accountPurpose}
                valueDetail={formData.accountPurposeDetail}
              />

              <TransactionVolumeField
                onChange={(value) => handleFieldChange('transactionVolume', value)}
                onDescriptionChange={(value) =>
                  handleFieldChange('transactionVolumeDescription', value)
                }
                value={formData.transactionVolume}
                valueDescription={formData.transactionVolumeDescription}
              />

              <SourceOfFundsField
                onChange={(value) => handleFieldChange('sourceOfFunds', value)}
                onDescriptionChange={(value) =>
                  handleFieldChange('sourceOfFundsDescription', value)
                }
                value={formData.sourceOfFunds}
                valueDescription={formData.sourceOfFundsDescription}
              />

              <RegulatedStatusField
                onChange={(value) => handleFieldChange('isRegulated', value)}
                onRegulatorNameChange={(value) => handleFieldChange('regulatorName', value)}
                regulatorName={formData.regulatorName}
                value={formData.isRegulated}
              />

              <USNexusField
                onChange={(value) => handleFieldChange('usNexus', value)}
                onDetailsChange={(value) => handleFieldChange('usNexusDetails', value)}
                value={formData.usNexus}
                valueDetails={formData.usNexusDetails}
              />
            </Stack>
          </Stack>
        </Box>

        {/* Submit Button */}
        <Box
          sx={(theme) => ({
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: theme.palette.background.grey.main,
            borderTop: `1px solid ${theme.palette.border.main}`,
            paddingY: 2,
            paddingX: 8,
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 10
          })}
        >
          <LoadingButton
            color="primary"
            disabled={!isFormValid}
            loading={loading}
            onClick={handleSubmitClick}
            size="large"
            variant="contained"
          >
            Submit
          </LoadingButton>
        </Box>
      </DialogContentStyled>
    </FormModal>
  );
};

AdditionalInfoModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default AdditionalInfoModal;
